import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ListTodo, Plus, Clock, Calendar, CheckCircle2, Circle, AlertTriangle,
  Loader2, ChevronDown, ChevronUp, MoreVertical, Trash2, Edit2, CheckCheck, Play
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdviserPicker from '@/components/shared/AdviserPicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApplyTemplateModal } from '@/components/dashboard/TaskTemplatesLibrary';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-red-100 text-red-700'
};

const CATEGORY_COLORS = {
  'follow-up': 'border-l-blue-500',
  'meeting': 'border-l-purple-500',
  'call': 'border-l-green-500',
  'documents': 'border-l-amber-500',
  'proposal': 'border-l-pink-500',
  'compliance': 'border-l-indigo-500',
  'other': 'border-l-gray-400'
};

function formatDueDate(dateString) {
  if (!dateString) return null;
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / 86400000);
  
  if (diffDays < 0) {
    return { text: `${Math.abs(diffDays)}d overdue`, isOverdue: true };
  } else if (diffDays === 0) {
    return { text: 'Due today', isDueToday: true };
  } else if (diffDays === 1) {
    return { text: 'Tomorrow', isUpcoming: true };
  } else if (diffDays <= 7) {
    return { text: `In ${diffDays} days`, isUpcoming: true };
  }
  
  return { text: date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) };
}

// Task Item Component
function TaskItem({ task, onComplete, onDelete, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const dueInfo = formatDueDate(task.dueDate);
  const isCompleted = task.status === 'completed';
  
  return (
    <div className={cn(
      "border rounded-lg bg-white overflow-hidden border-l-4 transition-all",
      CATEGORY_COLORS[task.category] || CATEGORY_COLORS.other,
      isCompleted && "opacity-60"
    )}>
      {/* Main Row */}
      <div className="p-3 flex items-start gap-3">
        <button
          onClick={() => onComplete(task.id)}
          className={cn(
            "mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
            isCompleted 
              ? "bg-green-500 border-green-500 text-white" 
              : "border-[#E6E0D9] hover:border-[#4A3B32]"
          )}
        >
          {isCompleted && <CheckCircle2 className="w-4 h-4" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className={cn(
                "font-medium text-sm",
                isCompleted ? "text-[#8C8078] line-through" : "text-[#2C2420]"
              )}>
                {task.title}
              </p>
              {task.description && (
                <p className="text-xs text-[#5D524D] mt-0.5 line-clamp-1">{task.description}</p>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {dueInfo && (
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-full flex items-center gap-1",
                  dueInfo.isOverdue ? "bg-red-100 text-red-700" :
                  dueInfo.isDueToday ? "bg-amber-100 text-amber-700" :
                  "bg-gray-100 text-gray-600"
                )}>
                  <Clock className="w-3 h-3" />
                  {dueInfo.text}
                </span>
              )}
              
              <span className={cn("text-xs px-2 py-0.5 rounded-full capitalize", PRIORITY_COLORS[task.priority])}>
                {task.priority}
              </span>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Collapse' : 'Expand'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDelete(task.id)} className="text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
      
      {/* Expanded Content - Checklist */}
      {expanded && task.checklist && task.checklist.length > 0 && (
        <div className="px-3 pb-3 pt-1 border-t border-[#E6E0D9]">
          <p className="text-xs font-medium text-[#8C8078] mb-2">Checklist</p>
          <div className="space-y-2">
            {task.checklist.map((item, index) => (
              <label key={index} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={item.completed}
                  onCheckedChange={() => onUpdate(task.id, index)}
                />
                <span className={cn(item.completed && "line-through text-[#8C8078]")}>
                  {item.item}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Tasks List Component
export function TasksList({ leadId, tasks, onRefresh }) {
  const { token } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [filter, setFilter] = useState('active'); // 'active', 'completed', 'all'
  
  const filteredTasks = tasks?.filter(task => {
    if (filter === 'active') return task.status !== 'completed';
    if (filter === 'completed') return task.status === 'completed';
    return true;
  }) || [];
  
  const handleComplete = async (taskId) => {
    try {
      await axios.put(`${API_BASE}/api/leads/tasks/${taskId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task completed');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };
  
  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/leads/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted');
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };
  
  const handleToggleChecklist = async (taskId, itemIndex) => {
    try {
      await axios.put(`${API_BASE}/api/leads/tasks/${taskId}/checklist/${itemIndex}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to update checklist');
    }
  };
  
  const activeTasks = tasks?.filter(t => t.status !== 'completed') || [];
  const overdueTasks = activeTasks.filter(t => {
    if (!t.dueDate) return false;
    return new Date(t.dueDate) < new Date();
  });
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-[#2C2420]">Tasks</h4>
          {overdueTasks.length > 0 && (
            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {overdueTasks.length} overdue
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowApplyTemplate(true)}
            data-testid="lead-apply-template-btn"
          >
            <Play className="w-3.5 h-3.5 mr-1" />
            Template
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Task
          </Button>
        </div>
      </div>
      
      {/* Filter Tabs */}
      <div className="flex gap-1 bg-[#F5F3F1] rounded-lg p-1">
        {['active', 'completed', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              "flex-1 px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-all",
              filter === f
                ? "bg-white text-[#2C2420] shadow-sm"
                : "text-[#5D524D] hover:text-[#2C2420]"
            )}
          >
            {f}
          </button>
        ))}
      </div>
      
      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-[#E6E0D9] rounded-lg">
          <ListTodo className="w-10 h-10 text-[#8C8078] mx-auto mb-2" />
          <p className="text-[#5D524D] text-sm">
            {filter === 'completed' ? 'No completed tasks' : 'No tasks yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onComplete={handleComplete}
              onDelete={handleDelete}
              onUpdate={handleToggleChecklist}
            />
          ))}
        </div>
      )}
      
      <AddTaskModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        leadId={leadId}
        onSuccess={onRefresh}
      />

      <ApplyTemplateModal
        open={showApplyTemplate}
        onClose={(applied) => {
          setShowApplyTemplate(false);
          if (applied) onRefresh?.();
        }}
        contextType="lead"
        contextId={leadId}
        contextName={`Lead`}
      />
    </div>
  );
}

// Add Task Modal
export function AddTaskModal({ isOpen, onClose, leadId, onSuccess }) {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('custom'); // 'custom' or 'template'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'follow-up',
    priority: 'medium',
    dueDate: '',
    assignedTo: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchOptions();
    }
  }, [isOpen]);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/leads/tasks/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch task options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (mode === 'template' && selectedTemplate) {
        await axios.post(`${API_BASE}/api/leads/tasks/from-template`, {
          leadId,
          templateId: selectedTemplate.id,
          dueDate: formData.dueDate || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        if (!formData.title.trim()) {
          toast.error('Please enter a task title');
          setSubmitting(false);
          return;
        }
        
        await axios.post(`${API_BASE}/api/leads/tasks`, {
          leadId,
          title: formData.title,
          description: formData.description || null,
          category: formData.category,
          priority: formData.priority,
          dueDate: formData.dueDate || null,
          assignedTo: formData.assignedTo?.trim() || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Task created');
      onSuccess?.();
      onClose();
      setFormData({ title: '', description: '', category: 'follow-up', priority: 'medium', dueDate: '', assignedTo: '' });
      setSelectedTemplate(null);
      setMode('custom');
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Task</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#4A3B32]" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Mode Toggle */}
            <div className="flex gap-2 p-1 bg-[#F5F3F1] rounded-lg">
              <button
                type="button"
                onClick={() => { setMode('custom'); setSelectedTemplate(null); }}
                className={cn(
                  "flex-1 py-2 rounded-md text-sm font-medium transition-all",
                  mode === 'custom' ? "bg-white shadow-sm text-[#2C2420]" : "text-[#5D524D]"
                )}
              >
                Custom Task
              </button>
              <button
                type="button"
                onClick={() => setMode('template')}
                className={cn(
                  "flex-1 py-2 rounded-md text-sm font-medium transition-all",
                  mode === 'template' ? "bg-white shadow-sm text-[#2C2420]" : "text-[#5D524D]"
                )}
              >
                From Template
              </button>
            </div>

            {mode === 'template' ? (
              /* Template Selection */
              <div className="space-y-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={cn(
                      "p-3 border rounded-lg cursor-pointer transition-all",
                      selectedTemplate?.id === template.id
                        ? "border-[#4A3B32] bg-[#4A3B32]/5"
                        : "border-[#E6E0D9] hover:border-[#C6A88F]"
                    )}
                  >
                    <p className="font-medium text-[#2C2420]">{template.name}</p>
                    <p className="text-xs text-[#5D524D] mt-0.5">{template.description}</p>
                    {template.checklist && (
                      <p className="text-xs text-[#8C8078] mt-1">
                        {template.checklist.length} checklist items
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Custom Task Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Task title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <AdviserPicker
                    value={formData.assignedTo || ''}
                    onChange={(v) => setFormData(prev => ({ ...prev, assignedTo: v.trim() }))}
                    placeholder="Choose internal adviser"
                    testId="lead-task-assignedto"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Add details..."
                    className="min-h-[60px]"
                  />
                </div>
              </form>
            )}

            {/* Due Date for template */}
            {mode === 'template' && selectedTemplate && (
              <div className="space-y-2">
                <Label>Due Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || (mode === 'template' && !selectedTemplate)}
                className="bg-[#4A3B32] hover:bg-[#3A2B22]"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Task'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TasksList;
